import React, { useEffect, useState } from "react";
import { Button, Chip, IconButton, Tooltip } from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AssignDialog from "./AssignDialog";
import { fetchAssignees, assignSubmission } from "./api";
import { LoadingState, StatTile } from "../../UI/States";
import { Notice } from "../ui";

const STATUS_COLORS = {
  New: "default",
  Assigned: "warning",
  "In Progress": "info",
  Complete: "success",
};

// One submission as a compact card. `member` is set when shown inside a
// member's column (enables quick-unassign for that person).
const FormCard = ({ submission, member, onOpen, onEdit, onUnassign, busy }) => (
  <div
    className="app-card cursor-pointer rounded-xl border p-3 transition hover:-translate-y-0.5 hover:border-sky-400"
    onClick={() => onOpen(submission._id)}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {submission.values?.contractorName || "Unnamed Contractor"}
        </p>
        <p className="app-muted truncate text-xs">
          {submission.values?.address ||
            (submission.values?.latLng ? "Pinned location" : "No address")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {member ? (
          <Tooltip title={`Unassign ${member.name}`}>
            <IconButton
              size="small"
              disabled={busy}
              aria-label={`Unassign ${member.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onUnassign(submission, member);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            startIcon={<GroupAddIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(submission);
            }}
          >
            Assign
          </Button>
        )}
      </div>
    </div>
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <Chip
        label={submission.status}
        color={STATUS_COLORS[submission.status] || "default"}
        size="small"
      />
      {submission.values?.requiredDate === "ASAP" && (
        <Chip label="ASAP" color="error" size="small" />
      )}
      {submission.approval?.state === "pending" && (
        <Chip label="Awaiting Approval" color="secondary" size="small" variant="outlined" />
      )}
      {member &&
        (submission.assigned_to || [])
          .filter((a) => a.id !== member.id)
          .map((a) => (
            <Chip key={a.id} label={a.name} size="small" variant="outlined" />
          ))}
    </div>
  </div>
);

// Assigner overview: who's working on what, who has capacity, and what's
// still unassigned — with quick assign/unassign right from the board.
const TeamWorkload = ({ submissions, onOpen, onChanged }) => {
  const [assignees, setAssignees] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAssignees()
      .then(setAssignees)
      .catch(() => setError("Could not load the team list."));
  }, []);

  const open = (submissions || []).filter((s) => s.status !== "Complete");
  const unassigned = open.filter((s) => (s.assigned_to || []).length === 0);
  const awaitingApproval = open.filter((s) => s.approval?.state === "pending");

  const members = (assignees || [])
    .map((a) => ({
      ...a,
      forms: open.filter((s) =>
        (s.assigned_to || []).some((x) => x.id === a.id)
      ),
    }))
    .sort((a, b) => b.forms.length - a.forms.length);
  const maxLoad = Math.max(1, ...members.map((m) => m.forms.length));

  const handleUnassign = async (submission, member) => {
    const key = `${submission._id}:${member.id}`;
    setBusyKey(key);
    setError(null);
    try {
      await assignSubmission(
        submission._id,
        (submission.assigned_to || []).filter((a) => a.id !== member.id)
      );
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile
          label="Open spec sheets"
          value={open.length}
          icon={AssignmentOutlinedIcon}
        />
        <StatTile
          label="Waiting to be assigned"
          value={unassigned.length}
          icon={PersonAddAltIcon}
          tone={unassigned.length > 0 ? "amber" : "default"}
        />
        <StatTile
          label="Awaiting sales approval"
          value={awaitingApproval.length}
          icon={PendingActionsIcon}
          tone={awaitingApproval.length > 0 ? "violet" : "default"}
        />
      </div>

      {error && (
        <Notice tone="rose" className="font-medium">
          {error}
        </Notice>
      )}

      {/* Unassigned pool */}
      <div>
        <h3 className="mb-3 text-lg font-semibold tabular-nums">
          Unassigned{unassigned.length > 0 ? ` (${unassigned.length})` : ""}
        </h3>
        {unassigned.length === 0 ? (
          <p className="app-muted app-surface rounded-xl border px-4 py-3 text-sm">
            Nothing waiting — every open spec sheet has someone on it.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {unassigned.map((s) => (
              <FormCard
                key={s._id}
                submission={s}
                onOpen={onOpen}
                onEdit={setAssignTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* Per-member columns */}
      <div>
        <h3 className="mb-3 text-lg font-semibold">Team</h3>
        {assignees === null ? (
          <LoadingState label="Loading team..." className="!py-10" />
        ) : members.length === 0 ? (
          <p className="app-muted app-surface rounded-xl border px-4 py-3 text-sm">
            Nobody holds the SBCF Team or SBCF Assigner role yet — add roles in
            User Management first.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {members.map((m) => (
              <div
                key={m.id}
                className="app-card flex flex-col rounded-2xl border p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{m.name}</p>
                    <p className="app-muted truncate text-xs">
                      {(m.roles || []).join(", ")}
                    </p>
                  </div>
                  <Chip
                    label={`${m.forms.length} active`}
                    size="small"
                    color={m.forms.length === 0 ? "success" : "default"}
                  />
                </div>

                {/* Relative load bar */}
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--app-pressed)]">
                  <div
                    className="h-full rounded-full bg-sky-500 transition-all"
                    style={{ width: `${(m.forms.length / maxLoad) * 100}%` }}
                  />
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  {m.forms.length === 0 ? (
                    <p className="app-muted text-sm">
                      No active assignments — has capacity.
                    </p>
                  ) : (
                    m.forms.map((s) => (
                      <FormCard
                        key={s._id}
                        submission={s}
                        member={m}
                        onOpen={onOpen}
                        onUnassign={handleUnassign}
                        busy={busyKey === `${s._id}:${m.id}`}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AssignDialog
        open={!!assignTarget}
        submission={assignTarget}
        onClose={() => setAssignTarget(null)}
        onSaved={() => onChanged()}
      />
    </div>
  );
};

export default TeamWorkload;
